"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import contentfulDeafultLoader from "@utils/imageLoader";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import { getImageProps } from "next/image";
import { FC, useCallback, useContext, useEffect, useRef, useState, useTransition } from "react";
import { CircularProgressbar } from "react-circular-progressbar";
// import "react-circular-progressbar/dist/styles.css";
// import "swiper/css";
// import "swiper/css/effect-fade";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
import { Autoplay, EffectFade } from "swiper/modules";
import { Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import AddToCalendarButton from "@/components/AddToCalendar/AddToCalendarButton";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ModalsContext } from "@/context/ModalsContext";
import { ctaClickTracking, eventSectionTracking } from "@/dataLayer/dataLayer";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import cn from "@/utils/cn";
import { countdownToEvent } from "@/utils/countdownToEvent";
import { formatDate } from "@/utils/dateUtils";

import { CountdownProps, EventLaunchCardProps } from "./types";

const Countdown: React.FC<CountdownProps> = ({ eventDate, earlyAccessCta, eventName, showLiveNowText }) => {
  const timeUntilEvent = countdownToEvent(eventDate as any);
  const EventLaunchButtonClasses = cn(
    "btn btn-primary-white btn-medium !border-white hover:!border-0 font-medium border-0 md:ml-auto w-fit mx-auto md:relative bottom-xxl md:bottom-0",
  );
  const t = useTranslations();
  const locale = useLocale();
  const { setGetInTouchModalOpen, setGetFormPageLocation } = useContext(ModalsContext);

  const trakingHandler = (eventName: string) => {
    const newDate = new Date(eventDate);
    const date = newDate.toDateString();

    eventSectionTracking(eventName, date);
    ctaClickTracking(earlyAccessCta.label, "EventLaunchCard", eventName);
  };

  // eventDate has arrived once the countdown bottoms out at zero; there is no separate end-date
  // field on this content type, so `showLiveNowText` is the CMS-editor's manual signal that the
  // event has actually ended (flipped off) vs. is currently happening (left on).
  const hasReachedEventDate = timeUntilEvent.days === 0 && timeUntilEvent.hours === 0 && timeUntilEvent.minutes === 0;
  const isExpired = hasReachedEventDate && !showLiveNowText;

  const getInTouchHandler = () => {
    ctaClickTracking(t("label.event.getInTouch"), "EventLaunchCard", eventName);
    setGetFormPageLocation("EventLaunchCard");
    setGetInTouchModalOpen(true);
  };

  return (
    <div className="flex flex-col countDown gap-xl md:mt-0 whitespace-nowrap md:w-fit md:flex-row md:gap-0 md:box-material-primary overflow-hidden text-center">
      {isExpired ? (
        <Button
          className={cn(EventLaunchButtonClasses, "text-center")}
          type="button"
          size={ButtonSize.MEDIUM}
          variant={ButtonVariant.PRIMARY}
          theme={ButtonTheme.WHITE}
          onClick={getInTouchHandler}
        >
          {t("label.event.getInTouch")}
        </Button>
      ) : hasReachedEventDate ? (
        <>
          <div className="liveEvent mr-m justify-center">
            <span className="border-2 border-white rounded-full bg-status-error w-s h-s" /> {t("label.event.liveNow")}
          </div>
          <Button
            href={getLink(earlyAccessCta, locale)}
            target="_self"
            className={cn(EventLaunchButtonClasses, "text-center")}
            type="button"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.WHITE}
            onClick={() => {
              trakingHandler(eventName || "");
            }}
          >
            {t("label.event.haveLook")}
          </Button>
        </>
      ) : (
        <>
          <div className="liveEvent mr-s justify-center">
            <span className="inline-flex items-center h-full font-normal border-r pr-s border-r-gray-50 text-s">
              {timeUntilEvent.days}
              {t("letter.timeUntilEvent.days")}
            </span>
            <span className="inline-flex items-center h-full font-normal border-r pr-s border-r-gray-50 text-s">
              {timeUntilEvent.hours} {t("letter.timeUntilEvent.hours")}
            </span>
            <span className="inline-flex items-center h-full font-normal pr-xs text-s">
              {timeUntilEvent.minutes}
              {t("letter.timeUntilEvent.minutes")}
            </span>
          </div>
          <Button
            href={getLink(earlyAccessCta, locale)}
            target="_self"
            className={EventLaunchButtonClasses}
            type="button"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.WHITE}
            customClass="text-center"
            onClick={() => {
              trakingHandler(eventName || "");
            }}
          >
            {earlyAccessCta.label}
          </Button>
        </>
      )}
    </div>
  );
};

const EventLaunchCard: FC<EventLaunchCardProps> = ({ entry }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isSo = useIsSaleScreen();
  const t = useTranslations();
  const inspectorMode = useInspectorMode(entry.id);
  const swiperRef = useRef<any>();
  const arrayBars: number[] = [];
  const [isPlay, setIsPlay] = useState<boolean>(true);
  const [whoPlying, setWhoPlaying] = useState<number>(0);

  const totalSlides = entry?.events?.length;

  entry.events?.forEach((i) => {
    arrayBars.push(0);
  });

  const [bars, setBars] = useState(arrayBars);
  const barsRef = useRef(arrayBars);
  const [thumbsSwiper, setThumbsSwiper] = useState<any | null>(null);

  const changeSlide = useCallback((index: number) => {
    swiperRef.current.swiper.slideTo(index);
  }, []);

  const handleThumbClick = (index: number) => {
    if (whoPlying === index && isPlay) {
      setIsPlay(false);
    } else {
      setIsPlay(true);
      const newBars = [...bars];
      for (let i = 0; i < newBars.length; i++) {
        newBars[i] = 0;
      }
      newBars[index] = bars[index] + 1;
      setWhoPlaying(index);
      setBars(newBars);
    }
  };

  useEffect(() => {
    barsRef.current = bars;
  }, [bars]);

  useEffect(() => {
    const updateBars = () => {
      const newBars = [...barsRef.current];
      for (let i = 0; i < newBars.length; i++) {
        if (newBars[i] !== 0) {
          newBars[i] > 0 ? newBars[i]++ : newBars[i];

          if (newBars[i] > 100) {
            newBars[i] = 0;
            setWhoPlaying(newBars[i]);

            const nextIndex = (i + 1) % totalSlides;
            newBars[nextIndex] = 1;

            changeSlide(nextIndex);
          }
        } else {
          setIsPlay(true);
        }
      }
      barsRef.current = newBars;
      setBars(newBars);
    };
    const intervalId = setInterval(() => {
      isPlay ? updateBars() : null;
    }, 100);

    return () => clearInterval(intervalId);
  }, [changeSlide, isPlay, totalSlides]);

  if (isSo) return null;

  // Define a function to handle setting the thumbsSwiper
  const handleSetThumbsSwiper = (swiper: any) => {
    setBars([1, 0, 0, 0]);
    setThumbsSwiper(swiper);
  };

  function toAbsolute(url: string): string | import("next/dist/shared/lib/get-img-props").StaticImport {
    throw new Error("Function not implemented.");
  }

  // Mux Playback IDs
  const MUX_DESKTOP_ID = "ZvgFoif6ZpewrB4eNJ5iHLSlmrTq6Ak014BZ01dq7gb4E";

  return (
    <div className="flex justify-center">
      <div className="relative w-full">
        <div
          className="absolute left-0 right-0 z-20 mx-auto top-xxl w-fit mt-xxl md:top-0 md:bottom-0 md:m-auto md:h-fit md:left-m md:right-auto"
          {...inspectorMode.getProps("events")}
        >
          <Swiper
            className="thumbs"
            modules={[Thumbs]}
            onSwiper={handleSetThumbsSwiper}
            slidesPerView={entry.events?.length}
            direction="horizontal"
            pagination={false}
            breakpoints={{
              1024: {
                direction: "vertical",
              },
            }}
          >
            {entry.events &&
              entry.events.length > 1 &&
              entry.events.map((eventLaunchCard, index) => (
                <SwiperSlide key={index}>
                  <div className="circleStories" key={`thumb-${index}`} onClick={() => handleThumbClick(index)}>
                    <div className="backgroundCircleStories">
                      <CircularProgressbar
                        className={`absolute z-50 !w-[80%] md:!w-full`}
                        value={bars[index]}
                        strokeWidth={4}
                        styles={{
                          trail: {
                            strokeWidth: 4, // Imposta lo spessore del trail
                            stroke: "#cccccc",
                          },
                          path: {
                            stroke: "#ffffff", // Imposta il colore della parte che si riempe
                            transition: bars[index] === 0 ? "none" : "stroke-dashoffset 0.5s ease 0s",
                          },
                        }}
                      />
                      <span className="absolute">
                        {bars[index] !== 0 && (
                          <Icon name={isPlay ? "pause" : "play"} className="stroke-white" size="24" title={isPlay ? "Pause" : "Play"} />
                        )}
                      </span>
                      <Image
                        className="rounded-full"
                        src={eventLaunchCard?.circleImage?.url}
                        alt={eventLaunchCard?.circleImage?.title || ""}
                        width={46}
                        height={46}
                      />
                    </div>
                  </div>
                  <div
                    className={`label-thumb max-w-[55px] max-h-[22px] font-secondary font-normal text-xs leading-3 text-white text-center overflow-hidden mx-auto my-[6px]`}
                  >
                    {eventLaunchCard?.circleImageText || eventLaunchCard?.eventLocation}
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
        </div>

        <Swiper
          ref={swiperRef}
          spaceBetween={30}
          effect={"fade"}
          navigation={false}
          pagination={false}
          mousewheel={{
            enabled: true,
            releaseOnEdges: true,
          }}
          modules={[Autoplay, EffectFade, Thumbs]}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          className="mySwiper"
        >
          {entry.events?.map((eventLaunchCard, index) => {
            // const playbackId = isMobile ? eventLaunchCard?.vodMobile : eventLaunchCard?.vodDesktop;
            const playbackId = isMobile
              ? eventLaunchCard?.vodMobile || eventLaunchCard?.vodDesktop
              : eventLaunchCard?.vodDesktop || eventLaunchCard?.vodMobile;

            const src = !playbackId ? eventLaunchCard?.video?.url : undefined;

            const poster = getImageProps({
              alt: "",
              width: 1024,
              height: 768,
              quality: 80,
              src: eventLaunchCard?.backupVideoImage?.url,
            });
            return (
              <SwiperSlide key={index}>
                <div className="relative w-full h-svh overflow-hidden">
                  <div className="absolute z-10 w-full h-full img-overlay-homepage-hero"></div>
                  {/* <div className="absolute z-10 w-full h-full bg-black-0/45"></div> */}
                  <video
                    className="videoHero"
                    width={eventLaunchCard?.video?.width}
                    height={eventLaunchCard?.video?.height}
                    poster={poster.props.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src={eventLaunchCard?.video?.url} type="video/mp4" />
                  </video>

                  <div className="absolute top-0 left-0 z-30 w-full h-full">
                    <div
                      // The story-circle thumbnails are always pinned to the physical left edge
                      // (md:left-m), regardless of locale. In RTL locales flex-row reverses its
                      // main axis, which would push this text block into that same left-hand
                      // area and overlap the thumbnails — dir="ltr" keeps the logo/text side by
                      // side in their original order so that never happens.
                      dir="ltr"
                      className={cn(
                        "flex md:w-19/24 flex-col items-center justify-center h-full md:flex-row mx-auto mt-[80px] md:mt-0",
                        eventLaunchCard?.logo?.url ? "md:flex-row" : "md:flex-col",
                      )}
                    >
                      <h3 className="md:hidden font-primary text-white uppercase text-center mb-l">{t("label.card.eventLaunch")}</h3>
                      {eventLaunchCard?.logo?.url && (
                        <div className="flex justify-center w-2/3 md:w-6/12 mb-xs">
                          <Image src={eventLaunchCard?.logo?.url} fit="pad" width={350} height={196} alt={eventLaunchCard?.logo?.title} />
                        </div>
                      )}

                      <div className={cn("flex flex-col md:w-6/12 gap-l", eventLaunchCard?.logo?.url ? "" : "items-center justify-center")}>
                        {eventLaunchCard?.title && (
                          <h3 className="hidden md:font-primary md:inline md:text-white uppercase md:text-lg">{eventLaunchCard?.title}</h3>
                        )}

                        <div className="flex flex-col items-center text-white whitespace-nowrap md:flex-row">
                          {eventLaunchCard?.eventDate && (
                            <p className="flex items-center mb-xs md:mb-0">
                              <Icon title="date" name="calendar" size="20" className="stroke-white" />
                              <span className="ml-[6px] md:mr-l">{formatDate(eventLaunchCard?.eventDate)}</span>
                            </p>
                          )}
                          {eventLaunchCard?.eventLocation && (
                            <p className="flex items-center">
                              <Icon title="position" name="geotag" size="20" className="stroke-white" />
                              <span className="ml-[6px]">{eventLaunchCard?.eventLocation}</span>
                            </p>
                          )}
                        </div>
                        {eventLaunchCard?.description && (
                          <div className={`block md:inline text-white text-center md:text-left pr-xl ${isMobile ? "pl-xl" : ""}`}>
                            <p>{eventLaunchCard?.description}</p>
                          </div>
                        )}

                        {eventLaunchCard?.eventDate && (
                          <Countdown
                            eventDate={eventLaunchCard?.eventDate}
                            eventName={eventLaunchCard?.title}
                            earlyAccessCta={eventLaunchCard?.applyForEarlyAccessCta}
                            showLiveNowText={eventLaunchCard?.showLiveNowText}
                          />
                        )}
                        {eventLaunchCard?.addToCalenderButton && (
                          <div className="flex justify-center md:justify-start sm:w-full mt-m md:mt-0 md:pl-s">
                            <AddToCalendarButton
                              label={eventLaunchCard?.secondaryCta?.label}
                              buttonClassName="font-primary text-center text-white underline underline-offset-4"
                              panelPosition={isMobile ? "top" : "bottom"}
                              event={{
                                title: eventLaunchCard?.title,
                                description: eventLaunchCard?.description,
                                location: eventLaunchCard?.eventLocation,
                                startDate: eventLaunchCard?.eventDate,
                              }}
                              onSelect={() => ctaClickTracking(eventLaunchCard?.secondaryCta?.label, "EventLaunchCard", eventLaunchCard?.title)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

export default EventLaunchCard;
